import { useCallback } from 'react';

const useSidebarLocking = ({ embedded, firstSessionCode, lockedSessionCodes, contentStatus, isPublicPreview = false }) => {
    // Only first module is "active" in the hierarchical sidebar UX.
    const isModuleActive = useCallback((moduleIndex) => isPublicPreview || moduleIndex === 0, [isPublicPreview]);

    // Inside the first module, only the first unit is considered active.
    const isUnitActive = useCallback(
        (moduleIndex, unitIndex) => isPublicPreview || (isModuleActive(moduleIndex) && unitIndex === 0),
        [isModuleActive, isPublicPreview]
    );

    const isSessionLocked = useCallback(
        (moduleIndex, unitIndex, sessionCode) => {
            if (isPublicPreview) return false;
            // 1) Lock by hierarchy (non-active module/unit branches)
            const inactiveByHierarchy = !isUnitActive(moduleIndex, unitIndex);
            // 2) Lock by embedded mode OR if it's not the very first session (even in non-embedded hierarchical view)
            const lockedBySessionLimit = (embedded || isUnitActive(moduleIndex, unitIndex)) && sessionCode !== firstSessionCode;
            // 3) Lock by plan limit (session_display_limit-derived set)
            const lockedByPlan = lockedSessionCodes.has(sessionCode);
            // A session is locked if any lock condition is true.
            return inactiveByHierarchy || lockedBySessionLimit || lockedByPlan;
        },
        [embedded, firstSessionCode, isPublicPreview, isUnitActive, lockedSessionCodes]
    );

    const getSessionStatus = useCallback(
        (sessionCode) => {
            if (isPublicPreview) return 'done';
            // contentStatus may store either a plain string or object with { status }.
            const statusData = contentStatus[sessionCode];
            return typeof statusData === 'object' ? statusData.status : statusData;
        },
        [contentStatus, isPublicPreview]
    );

    const getSessionUiState = useCallback(
        (moduleIndex, unitIndex, sessionCode) => {
            if (isPublicPreview) {
                return { locked: false, status: 'done', cursor: 'pointer', opacity: 1 };
            }
            // Centralized mapping from lock/status -> visual behavior.
            const locked = isSessionLocked(moduleIndex, unitIndex, sessionCode);
            const status = getSessionStatus(sessionCode);
            const cursor = locked ? 'not-allowed' : (status === 'done' ? 'pointer' : 'default');
            const opacity = locked ? 0.45 : (status === 'failed' ? 0.6 : 1);
            return { locked, status, cursor, opacity };
        },
        [getSessionStatus, isPublicPreview, isSessionLocked]
    );

    return {
        isModuleActive,
        isUnitActive,
        isSessionLocked,
        getSessionStatus,
        getSessionUiState,
    };
};

export const useSidebar = useSidebarLocking;
export { useSidebarLocking };
export default useSidebar;
